import { ChevronDown, Clock, Repeat } from 'lucide-react';
import React, { useState } from 'react';
import { MeetingRecurrence } from '../../types';
import DatePicker from '../forms/DatePicker';

interface RecurrenceSelectorProps {
  value?: MeetingRecurrence;
  onChange: (recurrence: MeetingRecurrence) => void;
  scheduleType: 'one_time' | 'daily' | 'weekly' | 'monthly' | 'custom';
  disabled?: boolean;
}

const RecurrenceSelector: React.FC<RecurrenceSelectorProps> = ({
  value,
  onChange,
  scheduleType,
  disabled = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const defaultRecurrence: MeetingRecurrence = {
    type: scheduleType === 'one_time' ? 'one_time' : scheduleType,
    interval: 1,
    daysOfWeek: [],
    endType: 'never',
    ...value,
  };

  const handleRecurrenceChange = (field: keyof MeetingRecurrence, fieldValue: any) => {
    const updated = { ...defaultRecurrence, [field]: fieldValue };
    onChange(updated);
  };

  if (scheduleType === 'one_time') {
    return null;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="flex items-center text-sm font-medium text-gray-700">
          <Repeat className="h-4 w-4 mr-2" />
          Recurrence Settings
        </label>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          disabled={disabled}
          className="flex items-center text-sm text-brand-primary hover:text-brand-primary-hover disabled:text-gray-400"
        >
          {isExpanded ? 'Hide' : 'Show'} Settings
          <ChevronDown
            className={`h-4 w-4 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {isExpanded && (
        <div className="bg-gray-50 rounded-lg p-4 space-y-4">
          {/* Interval */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Repeat Every</label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={defaultRecurrence.interval}
                  onChange={e => handleRecurrenceChange('interval', parseInt(e.target.value))}
                  disabled={disabled}
                  className="block w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary sm:text-sm disabled:bg-gray-50"
                />
                <span className="text-sm text-gray-700">
                  {scheduleType === 'daily'
                    ? 'day(s)'
                    : scheduleType === 'weekly'
                      ? 'week(s)'
                      : scheduleType === 'monthly'
                        ? 'month(s)'
                        : 'interval(s)'}
                </span>
              </div>
            </div>

            {/* Days of Week for Weekly */}
            {scheduleType === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Days of Week</label>
                <div className="flex flex-wrap gap-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        const daysOfWeek = defaultRecurrence.daysOfWeek || [];
                        const updated = daysOfWeek.includes(index)
                          ? daysOfWeek.filter((d: number) => d !== index)
                          : [...daysOfWeek, index];
                        handleRecurrenceChange('daysOfWeek', updated);
                      }}
                      disabled={disabled}
                      className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                        (defaultRecurrence.daysOfWeek || []).includes(index)
                          ? 'bg-brand-primary text-white border-brand-primary'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      } disabled:opacity-50`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Day of Month for Monthly */}
            {scheduleType === 'monthly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={defaultRecurrence.dayOfMonth || 1}
                  onChange={e => handleRecurrenceChange('dayOfMonth', parseInt(e.target.value))}
                  disabled={disabled}
                  className="block w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary sm:text-sm disabled:bg-gray-50"
                />
              </div>
            )}
          </div>

          {/* End Settings */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Clock className="h-4 w-4 inline mr-1" />
              End Recurrence
            </label>
            <div className="space-y-3">
              {/* Never */}
              <label className="flex items-center">
                <input
                  type="radio"
                  name="endType"
                  value="never"
                  checked={defaultRecurrence.endType === 'never'}
                  onChange={e => handleRecurrenceChange('endType', e.target.value)}
                  disabled={disabled}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Never</span>
              </label>

              {/* After */}
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="endType"
                  value="after"
                  checked={defaultRecurrence.endType === 'after'}
                  onChange={e => handleRecurrenceChange('endType', e.target.value)}
                  disabled={disabled}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">After</span>
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={defaultRecurrence.endAfterOccurrences || 1}
                  onChange={e =>
                    handleRecurrenceChange('endAfterOccurrences', parseInt(e.target.value))
                  }
                  disabled={disabled || defaultRecurrence.endType !== 'after'}
                  className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-brand-primary focus:border-brand-primary disabled:bg-gray-50"
                />
                <span className="text-sm text-gray-700">occurrences</span>
              </label>

              {/* Until */}
              <label className="flex items-center space-x-2">
                <input
                  type="radio"
                  name="endType"
                  value="until"
                  checked={defaultRecurrence.endType === 'until'}
                  onChange={e => handleRecurrenceChange('endType', e.target.value)}
                  disabled={disabled}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Until</span>
                <div className="flex items-center">
                  <DatePicker
                    value={
                      defaultRecurrence.endDate
                        ? new Date(defaultRecurrence.endDate).toISOString().split('T')[0]
                        : null
                    }
                    onChange={date => handleRecurrenceChange('endDate', date ? new Date(date) : undefined)}
                    disabled={disabled || defaultRecurrence.endType !== 'until'}
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecurrenceSelector;
